/**
 * Cumulative pacing chart (the prototype's hero visual): cumulative actual spend by day against a
 * straight-line budget pace, with the current run-rate projected to quarter end.
 */
import { useId, useMemo } from 'react'

import { formatMoney } from '@/lib/format'
import { useElementWidth } from '@/lib/useElementWidth'

import { labelForOffset, type QuarterCalendar } from '../lib/calendar'
import styles from './PaceChart.module.css'

interface PaceChartProps {
  /** `series[k]` = cumulative spend after k days (see cumulativeSeries). */
  series: number[]
  budget: number
  calendar: QuarterCalendar
  quarterLabel: string
  /** The period is over: final total, no projection line or label. */
  complete?: boolean
  /** Label of the as-of marker: "today" on the live view, else "as of". */
  markerLabel?: string
}

/** Default and minimum drawing width; the chart then follows its container's width. */
const DEFAULT_W = 760
const MIN_W = 600
const H = 250
const PAD = { l: 54, r: 84, t: 18, b: 30 }
const INNER_H = H - PAD.t - PAD.b
const MARKER_STEP = 7

function fixed(value: number): string {
  return value.toFixed(1)
}

export function PaceChart({
  series,
  budget,
  calendar,
  quarterLabel,
  complete = false,
  markerLabel = 'today',
}: PaceChartProps) {
  const rawId = useId()
  const gradientId = `pace-fill-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`
  // "Q3 budget" for a quarter; any other period just says "budget" (its label is in the title).
  const quarterShort = /^Q[1-4] \d{4}$/.test(quarterLabel) ? (quarterLabel.split(' ')[0] ?? '') : ''
  const budgetLabel = quarterShort ? `${quarterShort} budget` : 'budget'
  const [ref, W] = useElementWidth<SVGSVGElement>(DEFAULT_W, MIN_W)

  const geometry = useMemo(() => {
    const INNER_W = W - PAD.l - PAD.r
    const totalDays = calendar.totalDays
    const elapsed = Math.max(0, series.length - 1)
    const current = series[elapsed] ?? 0
    const projected = elapsed > 0 ? current * (totalDays / elapsed) : current
    // Credits can take the cumulative total (and its projection) below zero: scale both ways.
    const maxVal = Math.max(budget, projected, current, ...series, 1) * 1.1
    const minVal = Math.min(0, budget, projected, ...series) * 1.1
    const range = maxVal - minVal
    const x = (day: number) => PAD.l + (day / totalDays) * INNER_W
    const y = (value: number) => PAD.t + INNER_H * ((maxVal - value) / range)

    const actualPath = series.map((v, i) => `${i === 0 ? 'M' : 'L'}${fixed(x(i))},${fixed(y(v))}`).join(' ')
    const areaPath = `${actualPath} L${fixed(x(elapsed))},${fixed(y(0))} L${fixed(x(0))},${fixed(y(0))} Z`
    const paceLine = `M${fixed(x(0))},${fixed(y(0))} L${fixed(x(totalDays))},${fixed(y(budget))}`
    const projLine = `M${fixed(x(elapsed))},${fixed(y(current))} L${fixed(x(totalDays))},${fixed(y(projected))}`

    const markers: { key: number; cx: number; cy: number; label: string }[] = []
    for (let k = 0; k <= elapsed; k += MARKER_STEP) {
      markers.push({ key: k, cx: x(k), cy: y(series[k] ?? 0), label: labelForOffset(calendar, k) })
    }
    if (elapsed % MARKER_STEP !== 0) {
      markers.push({
        key: elapsed,
        cx: x(elapsed),
        cy: y(current),
        label: labelForOffset(calendar, Math.max(0, elapsed - 1)),
      })
    }

    const grid = [0.25, 0.5, 0.75, 1].map((f) => ({ f, y: PAD.t + INNER_H * (1 - f), label: minVal + range * f }))
    const zeroY = y(0)
    const monthTicks = calendar.monthStarts.map((day, i) => ({ day, x: x(day), label: calendar.monthLabels[i] ?? '' }))
    // Keep the two end labels from overprinting when budget and projection land close together.
    const budgetY = y(budget)
    const projectedY = y(projected)
    const gap = Math.abs(budgetY - projectedY)
    const nudge = gap < 12 ? (12 - gap) / 2 : 0
    const budgetLabelY = budgetY <= projectedY ? budgetY - nudge : budgetY + nudge
    const projectedLabelY = budgetY <= projectedY ? projectedY + nudge : projectedY - nudge
    const projectedGood = projected <= budget
    const onPace = current <= budget * (elapsed / totalDays)

    return {
      elapsed,
      current,
      projected,
      actualPath,
      areaPath,
      paceLine,
      projLine,
      markers,
      grid,
      monthTicks,
      todayX: x(elapsed),
      endX: x(totalDays),
      zeroY,
      endY: y(current),
      budgetY,
      projectedY,
      budgetLabelY,
      projectedLabelY,
      projectedGood,
      onPace,
    }
  }, [series, budget, calendar, W])

  const projTone = geometry.projectedGood ? 'Good' : 'Bad'
  const endTone = geometry.onPace ? 'Good' : 'Bad'
  const description = complete
    ? `Cumulative actual ${formatMoney(geometry.current)} for the full period; budget ${formatMoney(budget)}.`
    : `Cumulative actual ${formatMoney(geometry.current)} after ${geometry.elapsed} days; budget ${formatMoney(budget)}; projected ${formatMoney(geometry.projected)} at the current run-rate.`

  return (
    <svg
      ref={ref}
      className={styles.chart}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="xMinYMid meet"
      role="img"
      aria-label={`Spending pace. ${description}`}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" className={styles.gradTop} />
          <stop offset="100%" className={styles.gradBottom} />
        </linearGradient>
      </defs>
      {geometry.grid.map((g) => (
        <g key={g.f}>
          <line className={styles.axisLine} x1={PAD.l} y1={g.y} x2={W - PAD.r} y2={g.y} />
          <text className={styles.colLabel} x={PAD.l - 8} y={g.y + 3} textAnchor="end">
            {formatMoney(g.label, { compact: true })}
          </text>
        </g>
      ))}
      <line className={styles.axisLine} x1={PAD.l} y1={geometry.zeroY} x2={W - PAD.r} y2={geometry.zeroY} />
      {geometry.monthTicks.map((tick, i) => (
        <g key={tick.label}>
          <text className={styles.colLabel} x={tick.x + 2} y={H - 8} textAnchor="start">
            {tick.label}
          </text>
          {i > 0 ? <line className={styles.monthLine} x1={tick.x} y1={PAD.t} x2={tick.x} y2={PAD.t + INNER_H} /> : null}
        </g>
      ))}
      {complete ? null : (
        <>
          <line
            className={styles.todayLine}
            x1={geometry.todayX}
            y1={PAD.t}
            x2={geometry.todayX}
            y2={PAD.t + INNER_H}
          />
          <text className={styles.hint} x={geometry.todayX} y={PAD.t - 4} textAnchor="middle">
            {markerLabel}
          </text>
        </>
      )}
      <path d={geometry.paceLine} className={styles.paceLine} />
      <path d={geometry.areaPath} fill={`url(#${gradientId})`} stroke="none" />
      <path d={geometry.actualPath} className={styles.actualLine} />
      {complete ? null : <path d={geometry.projLine} className={`${styles.projLine} ${styles[`stroke${projTone}`]}`} />}
      {geometry.markers.map((m) => (
        <circle key={m.key} className={styles.marker} cx={fixed(m.cx)} cy={fixed(m.cy)} r={3.2}>
          <title>{`${m.label} ${calendar.year} — Cumulative actual: ${formatMoney(series[m.key] ?? 0)}`}</title>
        </circle>
      ))}
      <circle
        className={`${styles.endDot} ${styles[`fill${endTone}`]}`}
        cx={geometry.todayX}
        cy={geometry.endY}
        r={4.5}
      />
      <circle className={styles.targetDot} cx={geometry.endX} cy={geometry.budgetY} r={3.5} />
      <text className={styles.endLabel} x={geometry.endX + 6} y={geometry.budgetLabelY + 3}>
        {budgetLabel}
      </text>
      {complete ? null : (
        <>
          <circle className={styles[`fill${projTone}`]} cx={geometry.endX} cy={geometry.projectedY} r={3.5} />
          <text
            className={`${styles.endLabel} ${styles[`fill${projTone}`]}`}
            x={geometry.endX + 6}
            y={geometry.projectedLabelY + 3}
          >
            projected
          </text>
        </>
      )}
    </svg>
  )
}
